import { SessionApiError, apiGet, apiSend } from '../../session/client';

/**
 * Everything the Rewards page reads and writes, and nothing it invents.
 *
 * Each call is an existing PENNY route — the ones the Android wallet already
 * uses:
 *
 * - `consumer/penny` — the signed-in person's wallet: balances, the whole
 *   ledger history, their referral record, and the programme in force.
 * - `consumer/penny/referral` — their own referral code (made on first ask, so
 *   the page only asks when the person wants it, never on load).
 * - `consumer/penny/referral/claim` — say who invited you.
 * - `consumer/penny/vouchers/redeem` — a code that grants PENNY.
 * - `consumer/penny-public/offers` — live PENNY campaigns.
 *
 * There is no person id in any path or body: the server decides whose wallet it is.
 * Account credit, voucher stamp cards and the PENNY→credit swap have no domain
 * on the platform (owner decision D8 is open), so nothing here reaches for them.
 */

export type EarnRule =
  | { readonly method: 'PER_POUND'; readonly unitsPerPound: number }
  | { readonly method: 'PERCENT_OF_SPEND'; readonly percentBps: number };

export interface Programme {
  /** The programme's own name, as Just Treated wrote it (e.g. "… (TEST terms — not approved)"). */
  readonly name?: string;
  readonly versionNumber: number;
  readonly unit: string;
  readonly valueMinorPerUnit: number;
  readonly currency: string;
  readonly earn: EarnRule;
  readonly qualifyOn: 'ORDER_CONFIRMED' | 'ORDER_COMPLETED';
  readonly holdDays: number;
  readonly expiryDays: number;
  readonly redemption: {
    readonly minUnits: number;
    readonly maxPercentOfEligibleBps: number;
    readonly minPayableMinor: number;
  };
  readonly bookingCompletedUnits: number;
  readonly regulatedMedicines: string;
}

export interface HistoryLine {
  readonly id: string;
  readonly at: string;
  readonly type: string;
  readonly category: string;
  readonly units: number;
  readonly valueMinor: number;
  readonly currency: string;
  readonly direction: 'IN' | 'OUT' | 'STATE';
  readonly orderId?: string;
  readonly bookingId?: string;
  readonly reason?: string;
  readonly description: string;
}

export interface Wallet {
  readonly status: string;
  readonly unit: string;
  readonly pendingUnits: number;
  readonly availableUnits: number;
  readonly reservedUnits: number;
  readonly spendableUnits: number;
  readonly lifetimeEarnedUnits: number;
  readonly lifetimeRedeemedUnits: number;
  readonly expiringSoon: readonly { readonly units: number; readonly expiresAt: string }[];
  readonly valueMinorPerUnit: number | null;
  readonly currency: string | null;
  readonly history: readonly HistoryLine[];
  readonly referral: {
    readonly code: string | null;
    readonly referred: number;
    readonly qualified: number;
    readonly terms: {
      readonly referrerUnits: number;
      readonly referredUnits: number;
      readonly minQualifyingSpendMinor: number;
    } | null;
  };
  readonly programme: Programme | null;
}

export interface Campaign {
  readonly id: string;
  readonly name: string;
  readonly scope: string;
  readonly funding: string;
  readonly sponsorName?: string;
  readonly kind: 'MULTIPLIER' | 'BONUS_UNITS';
  readonly multiplier?: number;
  readonly bonusUnits?: number;
  readonly endsAt: string;
  readonly newCustomersOnly: boolean;
}

export const getWallet = (): Promise<Wallet> => apiGet<Wallet>('/consumer/penny');

export const getReferralCode = (): Promise<{ readonly code: string }> =>
  apiGet<{ code: string }>('/consumer/penny/referral');

export const claimReferral = (
  code: string,
): Promise<{ readonly status: string; readonly claimedAt: string }> =>
  apiSend('POST', '/consumer/penny/referral/claim', { code });

export const redeemVoucher = (code: string): Promise<{ readonly units: number }> =>
  apiSend('POST', '/consumer/penny/vouchers/redeem', { code });

export const getOffers = async (): Promise<readonly Campaign[]> =>
  (await apiGet<{ campaigns?: readonly Campaign[] }>('/consumer/penny-public/offers')).campaigns ?? [];

// -- The Rewards Card (W2) -------------------------------------------------------------------------------------

/** The person's Rewards Card: an identifier for their PENNY account. No balance, nothing about them. */
export interface RewardsCard {
  readonly id: string;
  readonly status: 'ACTIVE' | 'SUSPENDED' | 'REPLACED' | 'CLOSED';
  /** 16 digits. */
  readonly number: string;
  /** As printed: `9826 1234 5678 9012`. */
  readonly display: string;
  /** What the QR encodes: `JT1:<number>`. */
  readonly payload: string;
  readonly issuedAt: string;
  readonly version: number;
  /** Which wallets the card can be added to here and now (W4: Apple, W5: Google). Absent on a just-replaced card: reload it. */
  readonly wallets?: { readonly apple: boolean; readonly google?: boolean };
}

export type ReplaceReason = 'LOST' | 'STOLEN' | 'DAMAGED' | 'OTHER';

/**
 * `consumer/penny/card`. Null when the card is switched off here (the route
 * answers 404 while the platform flag `penny.card` is off): nothing to draw.
 */
export async function getCard(): Promise<RewardsCard | null> {
  try {
    return await apiGet<RewardsCard>('/consumer/penny/card');
  } catch (error) {
    if (error instanceof SessionApiError && error.status === 404) return null;
    throw error;
  }
}

export const replaceCard = (
  cardId: string,
  reason: ReplaceReason,
  expectedVersion: number,
): Promise<{ readonly replaced: RewardsCard; readonly card: RewardsCard }> =>
  apiSend('POST', `/consumer/penny/card/${encodeURIComponent(cardId)}/replace`, { reason, expectedVersion });

/** A 5-minute link to the person's own Apple Wallet pass (W4). Opened as a plain navigation: Safari hands it to Wallet. */
export const appleWalletLink = (): Promise<{
  readonly downloadPath: string;
  readonly expiresInSeconds: number;
}> => apiSend('POST', '/consumer/penny/card/apple-wallet', {});

/** W6: a counter's request to use some of this person's PENNY on a sale. */
export interface CounterSpendRequest {
  readonly id: string;
  readonly status: 'AWAITING_APPROVAL' | 'APPROVED';
  readonly businessName: string | null;
  readonly units: number;
  readonly valueMinor: number;
  readonly totalMinor: number;
  readonly payableMinor: number;
  readonly currency: string;
  readonly expiresAt: string;
  readonly requestedAt: string;
}

/** Requests waiting for this person. Empty while cards are switched off (404). */
export async function counterSpends(): Promise<readonly CounterSpendRequest[]> {
  try {
    return (await apiGet<{ spends: readonly CounterSpendRequest[] }>('/consumer/penny/counter-spends'))
      .spends;
  } catch (error) {
    if (error instanceof SessionApiError && error.status === 404) return [];
    throw error;
  }
}
const spendPath = (id: string, action: string) =>
  `/consumer/penny/counter-spends/${encodeURIComponent(id)}/${action}`;
export const approveCounterSpend = (id: string): Promise<unknown> =>
  apiSend('POST', spendPath(id, 'approve'), {});
export const declineCounterSpend = (id: string): Promise<unknown> =>
  apiSend('POST', spendPath(id, 'decline'), {});
export const counterSpendCode = (
  id: string,
): Promise<{ readonly code: string; readonly expiresAt: string }> =>
  apiSend('POST', spendPath(id, 'code'), {});

/** The person's own "Save to Google Wallet" link (W5): the server puts their card at Google first. */
export const googleWalletLink = (): Promise<{ readonly saveUrl: string }> =>
  apiSend('POST', '/consumer/penny/card/google-wallet', {});

/** Only ever Google's own save page: a link anywhere else is not followed. */
export const isGoogleSaveUrl = (url: string): boolean => url.startsWith('https://pay.google.com/gp/v/save/');

/** iPhone, iPad, or Safari on a Mac: where "Add to Apple Wallet" can work. */
export function canUseAppleWallet(userAgent: string): boolean {
  if (/iPhone|iPad|iPod/u.test(userAgent)) return true;
  return (
    /Macintosh/u.test(userAgent) &&
    /Safari\//u.test(userAgent) &&
    !/Chrome|Chromium|Edg|Firefox/u.test(userAgent)
  );
}
